import { useEffect, useState, useCallback, type ReactNode } from 'react';
import Editor from 'react-simple-code-editor';
import { highlight, languages } from 'prismjs';
import 'prismjs/components/prism-yaml';
import 'prismjs/themes/prism-tomorrow.css';
import yaml from 'js-yaml';
import { ChevronDown, ChevronRight, Plus, Trash2, Code, FormInput } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogBody,
  DialogFooter,
} from '../ui/Dialog';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { Badge } from '../ui/Badge';
import { apiClient } from '../../lib/api-client';
import { WORKFLOW_TAXONOMY_PROMPT_KEYS } from './workflow-prompt-fields';
import type { ToolRef } from './TaxonomyToolsEditor';

// ---------------------------------------------------------------------------
// Types (mirrors backend)
// ---------------------------------------------------------------------------

interface TenantBotConfig {
  enabled: boolean;
  displayName: string;
  defaultChannelId?: string;
  escalationChannelId?: string;
}

interface TenantInstance {
  code: string;
  displayName?: string;
  agentTitle?: string;
  agentDescription?: string;
  parentRef?: string;
  adoProject: string;
  adoAreaPath?: string;
  repo: string;
  bot: TenantBotConfig;
}

interface TeamTypeEntry {
  id: string;
  tenants: TenantInstance[];
}

interface ProductConfig {
  id: string;
  displayName?: string;
  teamTypes: TeamTypeEntry[];
}

interface ProductDefinition {
  id: string;
  displayName?: string;
}

interface TeamDefinition {
  id: string;
  displayName?: string;
}

interface TenantDefinition {
  code: string;
  displayName?: string;
}

interface WorkflowType {
  id: string;
  label: string;
  executionMode: 'auto' | 'draft' | 'assist';
  stepExecution?: 'independent_agents' | 'same_chat_followups';
  repoType: string;
  reviewGate: string;
  reviewMode?: 'none' | 'human' | 'ai';
  description?: string;
  publish?: {
    steps: Array<{
      type: string;
      target?: string;
      condition?: string;
    }>;
  };
  prompts?: PromptFields;
  tools?: ToolRef[];
}

interface OutputTarget {
  type: string;
  target: string;
  format?: string;
}

type RequestComplexity = 'simple' | 'medium' | 'complex';

interface ComplexityModelMap {
  simple: string;
  medium: string;
  complex: string;
}

interface ModelRoutingPolicy {
  lobbyConversation: ComplexityModelMap;
  lobbyQuickWrite: ComplexityModelMap;
  planner: ComplexityModelMap;
  triager: ComplexityModelMap;
  cursorExecution: ComplexityModelMap;
  reviewer: ComplexityModelMap;
}

interface LobbyCapabilityPolicy {
  quickWriteMaxMinutes: number;
  quickWriteMaxToolCalls: number;
  allowRepoChanges: boolean;
  allowPrMerge: boolean;
  allowedWriteActions: string[];
}

interface AdditionalRepositoryAccessEntry {
  repository: string;
  description?: string;
  access: 'read_only';
  capabilities: string[];
}

interface RepositoryAccessPolicy {
  additionalRepositories: AdditionalRepositoryAccessEntry[];
}

interface PromptFields {
  classifierSystemPrompt?: string;
  contextTriageSystemPrompt?: string;
  plannerSystemPrompt?: string;
  authorSystemPrompt?: string;
  reviewerSystemPrompt?: string;
  conversationSystemPrompt?: string;
  lobbySystemPrompt?: string;
  executionSystemPrompt?: string;
  executionFollowupSystemPrompt?: string;
  quickWriteSystemPrompt?: string;
  ingestSystemPrompt?: string;
  triageFeedbackPrompt?: string;
  hierarchySystemPrompt?: string;
  publishSystemPrompt?: string;
  escalationPrompt?: string;
}

export interface StreamFormData {
  streamId: string;
  displayName: string;
  enabled: boolean;
  products: ProductConfig[];
  productDefinitions: ProductDefinition[];
  teamDefinitions: TeamDefinition[];
  tenantDefinitions: TenantDefinition[];
  taxonomy: { workflowTypes: WorkflowType[] };
  triage: { classifierModel: string; classifierTimeoutMs: number; conversationModel?: string; agentModel?: string; lanes: { proceed_window_minutes: number; escalation_timeout_hours: number } };
  modelRouting: ModelRoutingPolicy;
  lobbyPolicy: LobbyCapabilityPolicy;
  review: { defaultReviewerRole: string; reviewTimeoutHours: number; reminderAfterHours: number };
  repositoryAccess: RepositoryAccessPolicy;
  outputTargets: OutputTarget[];
  prompts: PromptFields;
  secrets: Record<string, string>;
  learningEnabled: boolean;
  evolutionEnabled: boolean;
  emailInboxes?: any[];
  meetingRecordings?: any;
  jiraListeners?: any[];
}

// ---------------------------------------------------------------------------
// Collapsible section helper
// ---------------------------------------------------------------------------

function Section({
  title,
  badge,
  defaultOpen = false,
  children,
  actions,
}: {
  title: string;
  badge?: string;
  defaultOpen?: boolean;
  children: ReactNode;
  actions?: ReactNode;
}) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="border border-gray-200 dark:border-gray-700 rounded-lg">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="w-full flex items-center gap-2 px-3 py-2 text-left text-sm font-medium text-gray-900 dark:text-gray-100 hover:bg-gray-50 dark:hover:bg-gray-800/50 rounded-t-lg"
      >
        {open ? <ChevronDown className="w-4 h-4 shrink-0" /> : <ChevronRight className="w-4 h-4 shrink-0" />}
        <span className="flex-1">{title}</span>
        {badge && <Badge variant="outline" className="text-xs">{badge}</Badge>}
        {actions && <span onClick={(e) => e.stopPropagation()}>{actions}</span>}
      </button>
      {open && <div className="px-3 pb-3 pt-1 space-y-3">{children}</div>}
    </div>
  );
}

/** One workflow type in stream taxonomy — collapsed by default; new empty rows open for editing. */
function CollapsibleWorkflowTypeCard({
  defaultOpen = false,
  summaryTitle,
  workflowId,
  onRemove,
  children,
}: {
  defaultOpen?: boolean;
  summaryTitle: string;
  workflowId?: string;
  onRemove: () => void;
  children: ReactNode;
}) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="border border-gray-100 dark:border-gray-700/50 rounded-md bg-white dark:bg-gray-900/50 overflow-hidden">
      <div className="flex items-stretch gap-1 border-b border-gray-100 dark:border-gray-700/50 bg-gray-50/90 dark:bg-gray-800/50">
        <button
          type="button"
          onClick={() => setOpen((o) => !o)}
          className="flex flex-1 items-center gap-2 min-w-0 px-2 py-2 text-left rounded-none hover:bg-gray-100/90 dark:hover:bg-gray-700/40"
        >
          {open ? (
            <ChevronDown className="w-4 h-4 shrink-0 text-gray-500 dark:text-gray-400" />
          ) : (
            <ChevronRight className="w-4 h-4 shrink-0 text-gray-500 dark:text-gray-400" />
          )}
          <span className="text-xs font-semibold text-gray-800 dark:text-gray-100 truncate">{summaryTitle}</span>
          {workflowId ? (
            <Badge variant="outline" className="text-[10px] font-mono shrink-0 max-w-[12rem] truncate">
              {workflowId}
            </Badge>
          ) : null}
        </button>
        <button
          type="button"
          onClick={onRemove}
          className="text-red-500 hover:text-red-700 shrink-0 px-2 flex items-center"
          title="Remove workflow type"
        >
          <Trash2 className="w-3.5 h-3.5" />
        </button>
      </div>
      {open && <div className="p-2.5 space-y-2">{children}</div>}
    </div>
  );
}

function FieldRow({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="grid grid-cols-[140px_1fr] gap-2 items-start">
      <label className="text-xs font-medium text-gray-500 dark:text-gray-400 pt-2 truncate">{label}</label>
      <div>{children}</div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Form view
// ---------------------------------------------------------------------------

export function StreamFormView({
  data,
  onChange,
}: {
  data: StreamFormData;
  onChange: (d: StreamFormData) => void;
}) {
  const u = (patch: Partial<StreamFormData>) => onChange({ ...data, ...patch });

  // -- Flat entity definitions helpers --
  const updateProductDefinition = (idx: number, patch: Partial<ProductDefinition>) => {
    const updated = data.productDefinitions.map((entry, i) => (i === idx ? { ...entry, ...patch } : entry));
    u({ productDefinitions: updated });
  };
  const addProductDefinition = () => {
    u({ productDefinitions: [...data.productDefinitions, { id: '', displayName: '' }] });
  };
  const removeProductDefinition = (idx: number) => {
    u({ productDefinitions: data.productDefinitions.filter((_, i) => i !== idx) });
  };

  const updateTeamDefinition = (idx: number, patch: Partial<TeamDefinition>) => {
    const updated = data.teamDefinitions.map((entry, i) => (i === idx ? { ...entry, ...patch } : entry));
    u({ teamDefinitions: updated });
  };
  const addTeamDefinition = () => {
    u({ teamDefinitions: [...data.teamDefinitions, { id: '', displayName: '' }] });
  };
  const removeTeamDefinition = (idx: number) => {
    u({ teamDefinitions: data.teamDefinitions.filter((_, i) => i !== idx) });
  };

  const updateTenantDefinition = (idx: number, patch: Partial<TenantDefinition>) => {
    const updated = data.tenantDefinitions.map((entry, i) => (i === idx ? { ...entry, ...patch } : entry));
    u({ tenantDefinitions: updated });
  };
  const addTenantDefinition = () => {
    u({ tenantDefinitions: [...data.tenantDefinitions, { code: '', displayName: '' }] });
  };
  const removeTenantDefinition = (idx: number) => {
    u({ tenantDefinitions: data.tenantDefinitions.filter((_, i) => i !== idx) });
  };

  // -- Secrets helpers --
  const secretEntries = Object.entries(data.secrets);
  const addSecret = () => u({ secrets: { ...data.secrets, '': '' } });
  const updateSecretKey = (oldKey: string, newKey: string) => {
    const entries = Object.entries(data.secrets);
    const rebuilt: Record<string, string> = {};
    for (const [k, v] of entries) {
      rebuilt[k === oldKey ? newKey : k] = v;
    }
    u({ secrets: rebuilt });
  };
  const updateSecretValue = (key: string, value: string) => {
    u({ secrets: { ...data.secrets, [key]: value } });
  };
  const removeSecret = (key: string) => {
    const copy = { ...data.secrets };
    delete copy[key];
    u({ secrets: copy });
  };

  // -- Taxonomy helpers --
  const workflowTypes = data.taxonomy?.workflowTypes || [];
  const updateWorkflowType = (idx: number, patch: Partial<WorkflowType>) => {
    const updated = workflowTypes.map((wt, i) => (i === idx ? { ...wt, ...patch } : wt));
    u({ taxonomy: { ...data.taxonomy, workflowTypes: updated } });
  };
  const addWorkflowType = () => {
    u({ taxonomy: { ...data.taxonomy, workflowTypes: [...workflowTypes, { id: '', label: '', executionMode: 'draft', stepExecution: 'independent_agents', repoType: 'project', reviewGate: '', description: '' }] } });
  };
  const removeWorkflowType = (idx: number) => {
    u({ taxonomy: { ...data.taxonomy, workflowTypes: workflowTypes.filter((_, i) => i !== idx) } });
  };
  const updateWorkflowTypePrompt = (idx: number, key: keyof PromptFields, value: string) => {
    const current = workflowTypes[idx];
    updateWorkflowType(idx, {
      prompts: {
        ...(current?.prompts || {}),
        [key]: value || undefined,
      },
    });
  };
  const updateWorkflowTypeToolsFromJson = (idx: number, jsonText: string) => {
    if (!jsonText.trim()) {
      updateWorkflowType(idx, { tools: undefined });
      return;
    }
    try {
      const parsed = JSON.parse(jsonText);
      if (!Array.isArray(parsed)) return;
      updateWorkflowType(idx, { tools: parsed as ToolRef[] });
    } catch {
      // keep invalid draft text in the editor; do not clobber existing value
    }
  };
  const addWorkflowPublishStep = (idx: number) => {
    const current = workflowTypes[idx];
    const steps = current?.publish?.steps || [];
    updateWorkflowType(idx, {
      publish: { steps: [...steps, { type: '', target: '', condition: '' }] },
    });
  };
  const updateWorkflowPublishStep = (
    idx: number,
    stepIdx: number,
    patch: Partial<{ type: string; target?: string; condition?: string }>,
  ) => {
    const current = workflowTypes[idx];
    const steps = (current?.publish?.steps || []).map((step, i) => (i === stepIdx ? { ...step, ...patch } : step));
    updateWorkflowType(idx, { publish: { steps } });
  };
  const removeWorkflowPublishStep = (idx: number, stepIdx: number) => {
    const current = workflowTypes[idx];
    const steps = (current?.publish?.steps || []).filter((_, i) => i !== stepIdx);
    updateWorkflowType(idx, { publish: { steps } });
  };

  // -- Output targets helpers --
  const outputTargets = data.outputTargets || [];
  const updateOutputTarget = (idx: number, patch: Partial<OutputTarget>) => {
    const updated = outputTargets.map((ot, i) => (i === idx ? { ...ot, ...patch } : ot));
    u({ outputTargets: updated });
  };
  const addOutputTarget = () => {
    u({ outputTargets: [...outputTargets, { type: '', target: '', format: '' }] });
  };
  const removeOutputTarget = (idx: number) => {
    u({ outputTargets: outputTargets.filter((_, i) => i !== idx) });
  };

  const updateModelRole = (
    role: keyof ModelRoutingPolicy,
    complexity: RequestComplexity,
    value: string,
  ) => {
    u({
      modelRouting: {
        ...data.modelRouting,
        [role]: {
          ...data.modelRouting[role],
          [complexity]: value,
        },
      },
    });
  };
  const flatEntityBadge = [
    `${data.productDefinitions.length} product${data.productDefinitions.length !== 1 ? 's' : ''}`,
    `${data.teamDefinitions.length} team${data.teamDefinitions.length !== 1 ? 's' : ''}`,
    `${data.tenantDefinitions.length} tenant${data.tenantDefinitions.length !== 1 ? 's' : ''}`,
  ].join(' • ');

  return (
    <div className="space-y-4">
      {/* General */}
      <Section title="General">
        <FieldRow label="Stream ID">
          <Input value={data.streamId} disabled className="text-sm bg-gray-50 dark:bg-gray-800/50" />
        </FieldRow>
        <FieldRow label="Display Name">
          <Input value={data.displayName} onChange={(e) => u({ displayName: e.target.value })} className="text-sm" />
        </FieldRow>
        <FieldRow label="Enabled">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={data.enabled}
              onChange={(e) => u({ enabled: e.target.checked })}
              className="rounded border-gray-300 dark:border-gray-600"
            />
            <span className="text-sm text-gray-700 dark:text-gray-300">{data.enabled ? 'Active' : 'Paused'}</span>
          </label>
        </FieldRow>
        <FieldRow label="Learning">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={data.learningEnabled}
              onChange={(e) => u({ learningEnabled: e.target.checked })}
              className="rounded border-gray-300 dark:border-gray-600"
            />
            <span className="text-sm text-gray-700 dark:text-gray-300">Enabled</span>
          </label>
        </FieldRow>
        <FieldRow label="Evolution">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={data.evolutionEnabled}
              onChange={(e) => u({ evolutionEnabled: e.target.checked })}
              className="rounded border-gray-300 dark:border-gray-600"
            />
            <span className="text-sm text-gray-700 dark:text-gray-300">Enabled</span>
          </label>
        </FieldRow>
      </Section>

      <Section
        title="Products, Teams, Tenants"
        badge={flatEntityBadge}
        defaultOpen
      >
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
          <div className="space-y-2 border border-gray-100 dark:border-gray-700/50 rounded-md p-2.5 bg-white dark:bg-gray-900/50">
            <div className="flex items-center justify-between">
              <span className="text-xs font-semibold text-gray-700 dark:text-gray-300">Products</span>
              <button type="button" onClick={addProductDefinition} className="flex items-center gap-1 text-xs text-blue-600 dark:text-blue-400 hover:underline">
                <Plus className="w-3 h-3" /> Add
              </button>
            </div>
            {data.productDefinitions.map((entry, idx) => (
              <div key={`product-def-${idx}`} className="grid grid-cols-[1fr_1fr_auto] gap-1">
                <Input
                  value={entry.id}
                  onChange={(e) => updateProductDefinition(idx, { id: e.target.value })}
                  placeholder="id"
                  className="text-xs !py-1"
                />
                <Input
                  value={entry.displayName || ''}
                  onChange={(e) => updateProductDefinition(idx, { displayName: e.target.value || undefined })}
                  placeholder="display name"
                  className="text-xs !py-1"
                />
                <button type="button" onClick={() => removeProductDefinition(idx)} className="text-red-500 hover:text-red-700 px-1">
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            ))}
          </div>

          <div className="space-y-2 border border-gray-100 dark:border-gray-700/50 rounded-md p-2.5 bg-white dark:bg-gray-900/50">
            <div className="flex items-center justify-between">
              <span className="text-xs font-semibold text-gray-700 dark:text-gray-300">Teams</span>
              <button type="button" onClick={addTeamDefinition} className="flex items-center gap-1 text-xs text-blue-600 dark:text-blue-400 hover:underline">
                <Plus className="w-3 h-3" /> Add
              </button>
            </div>
            {data.teamDefinitions.map((entry, idx) => (
              <div key={`team-def-${idx}`} className="grid grid-cols-[1fr_1fr_auto] gap-1">
                <Input
                  value={entry.id}
                  onChange={(e) => updateTeamDefinition(idx, { id: e.target.value })}
                  placeholder="id"
                  className="text-xs !py-1"
                />
                <Input
                  value={entry.displayName || ''}
                  onChange={(e) => updateTeamDefinition(idx, { displayName: e.target.value || undefined })}
                  placeholder="display name"
                  className="text-xs !py-1"
                />
                <button type="button" onClick={() => removeTeamDefinition(idx)} className="text-red-500 hover:text-red-700 px-1">
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            ))}
          </div>

          <div className="space-y-2 border border-gray-100 dark:border-gray-700/50 rounded-md p-2.5 bg-white dark:bg-gray-900/50">
            <div className="flex items-center justify-between">
              <span className="text-xs font-semibold text-gray-700 dark:text-gray-300">Tenants</span>
              <button type="button" onClick={addTenantDefinition} className="flex items-center gap-1 text-xs text-blue-600 dark:text-blue-400 hover:underline">
                <Plus className="w-3 h-3" /> Add
              </button>
            </div>
            {data.tenantDefinitions.map((entry, idx) => (
              <div key={`tenant-def-${idx}`} className="grid grid-cols-[1fr_1fr_auto] gap-1">
                <Input
                  value={entry.code}
                  onChange={(e) => updateTenantDefinition(idx, { code: e.target.value })}
                  placeholder="code"
                  className="text-xs !py-1"
                />
                <Input
                  value={entry.displayName || ''}
                  onChange={(e) => updateTenantDefinition(idx, { displayName: e.target.value || undefined })}
                  placeholder="display name"
                  className="text-xs !py-1"
                />
                <button type="button" onClick={() => removeTenantDefinition(idx)} className="text-red-500 hover:text-red-700 px-1">
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            ))}
          </div>
        </div>
      </Section>

      {/* Taxonomy */}
      <Section
        title="Taxonomy"
        badge={`${workflowTypes.length} workflow type${workflowTypes.length !== 1 ? 's' : ''}`}
        actions={
          <button
            type="button"
            onClick={addWorkflowType}
            className="flex items-center gap-1 text-xs text-blue-600 dark:text-blue-400 hover:underline px-1"
          >
            <Plus className="w-3 h-3" /> Workflow Type
          </button>
        }
      >
        {workflowTypes.length === 0 && (
          <p className="text-xs text-gray-400 italic">No workflow types defined.</p>
        )}
        {workflowTypes.map((wt, i) => (
          <CollapsibleWorkflowTypeCard
            key={i}
            defaultOpen={!(String(wt.id || '').trim() || String(wt.label || '').trim())}
            summaryTitle={wt.label || wt.id || `Type ${i + 1}`}
            workflowId={
              String(wt.label || '').trim() && String(wt.id || '').trim() ? wt.id : undefined
            }
            onRemove={() => removeWorkflowType(i)}
          >
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="text-[11px] text-gray-400">ID</label>
                <Input value={wt.id} onChange={(e) => updateWorkflowType(i, { id: e.target.value })} className="text-xs !py-1" />
              </div>
              <div>
                <label className="text-[11px] text-gray-400">Label</label>
                <Input value={wt.label} onChange={(e) => updateWorkflowType(i, { label: e.target.value })} className="text-xs !py-1" />
              </div>
              <div>
                <label className="text-[11px] text-gray-400">Execution Mode</label>
                <select
                  value={wt.executionMode}
                  onChange={(e) => updateWorkflowType(i, { executionMode: e.target.value as 'auto' | 'draft' | 'assist' })}
                  className="w-full px-2 py-1 border rounded-md bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-gray-100 text-xs"
                >
                  <option value="draft">Draft — agent creates, human reviews</option>
                  <option value="auto">Auto — agent executes autonomously</option>
                  <option value="assist">Assist — agent supports, human drives</option>
                </select>
              </div>
              <div>
                <label className="text-[11px] text-gray-400">Repo Type</label>
                <Input
                  value={wt.repoType}
                  onChange={(e) => updateWorkflowType(i, { repoType: e.target.value })}
                  placeholder="e.g. project, technical, support"
                  className="text-xs !py-1"
                />
              </div>
              <div>
                <label className="text-[11px] text-gray-400">Step Execution</label>
                <select
                  value={wt.stepExecution || 'independent_agents'}
                  onChange={(e) => updateWorkflowType(i, {
                    stepExecution: e.target.value as 'independent_agents' | 'same_chat_followups',
                  })}
                  className="w-full px-2 py-1 border rounded-md bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-gray-100 text-xs"
                >
                  <option value="independent_agents">independent_agents</option>
                  <option value="same_chat_followups">same_chat_followups</option>
                </select>
              </div>
              <div>
                <label className="text-[11px] text-gray-400">Review Gate</label>
                <Input
                  value={wt.reviewGate}
                  onChange={(e) => updateWorkflowType(i, { reviewGate: e.target.value })}
                  placeholder="e.g. tech_lead, ba_lead, informational"
                  className="text-xs !py-1"
                />
              </div>
              <div>
                <label className="text-[11px] text-gray-400">Review Mode</label>
                <select
                  value={wt.reviewMode || 'human'}
                  onChange={(e) => updateWorkflowType(i, { reviewMode: e.target.value as 'none' | 'human' | 'ai' })}
                  className="w-full px-2 py-1 border rounded-md bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-gray-100 text-xs"
                >
                  <option value="none">none</option>
                  <option value="human">human</option>
                  <option value="ai">ai</option>
                </select>
              </div>
              <div>
                <label className="text-[11px] text-gray-400">Description</label>
                <Input
                  value={wt.description || ''}
                  onChange={(e) => updateWorkflowType(i, { description: e.target.value || undefined })}
                  placeholder="What this workflow type does"
                  className="text-xs !py-1"
                />
              </div>
            </div>

            <div className="border border-gray-100 dark:border-gray-700/50 rounded-md p-2 space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-[11px] font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">Publish Steps</span>
                <button
                  type="button"
                  onClick={() => addWorkflowPublishStep(i)}
                  className="flex items-center gap-1 text-xs text-blue-600 dark:text-blue-400 hover:underline"
                >
                  <Plus className="w-3 h-3" /> Step
                </button>
              </div>
              {(wt.publish?.steps || []).length === 0 && (
                <p className="text-xs text-gray-400 italic">No publish steps configured.</p>
              )}
              {(wt.publish?.steps || []).map((step, stepIdx) => (
                <div key={stepIdx} className="grid grid-cols-4 gap-2 items-end">
                  <div>
                    <label className="text-[11px] text-gray-400">Type</label>
                    <Input
                      value={step.type}
                      onChange={(e) => updateWorkflowPublishStep(i, stepIdx, { type: e.target.value })}
                      className="text-xs !py-1"
                    />
                  </div>
                  <div>
                    <label className="text-[11px] text-gray-400">Target</label>
                    <Input
                      value={step.target || ''}
                      onChange={(e) => updateWorkflowPublishStep(i, stepIdx, { target: e.target.value || undefined })}
                      className="text-xs !py-1"
                    />
                  </div>
                  <div>
                    <label className="text-[11px] text-gray-400">Condition</label>
                    <Input
                      value={step.condition || ''}
                      onChange={(e) => updateWorkflowPublishStep(i, stepIdx, { condition: e.target.value || undefined })}
                      className="text-xs !py-1"
                    />
                  </div>
                  <button
                    type="button"
                    onClick={() => removeWorkflowPublishStep(i, stepIdx)}
                    className="text-red-500 hover:text-red-700"
                    title="Remove step"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              ))}
            </div>

            <div className="border border-gray-100 dark:border-gray-700/50 rounded-md p-2 space-y-2">
              <span className="text-[11px] font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">Workflow Prompts</span>
              {(WORKFLOW_TAXONOMY_PROMPT_KEYS as Array<keyof PromptFields>).map((promptKey) => (
                <div key={promptKey}>
                  <label className="text-[11px] text-gray-400">{promptKey}</label>
                  <textarea
                    value={wt.prompts?.[promptKey] || ''}
                    onChange={(e) => updateWorkflowTypePrompt(i, promptKey, e.target.value)}
                    rows={2}
                    className="w-full px-2 py-1 border rounded-md bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-gray-100 text-xs focus:outline-none focus:ring-2 focus:ring-blue-500 resize-y"
                  />
                </div>
              ))}
            </div>

            <div className="border border-gray-100 dark:border-gray-700/50 rounded-md p-2 space-y-2">
              <span className="text-[11px] font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">Workflow Tools</span>
              <p className="text-[11px] text-gray-500 dark:text-gray-400">
                Optional per-workflow tool overlay/replace entries (`toolId`, `enabled`, `mode`, optional `capabilities`, `config`).
              </p>
              <textarea
                value={wt.tools?.length ? JSON.stringify(wt.tools, null, 2) : ''}
                onChange={(e) => updateWorkflowTypeToolsFromJson(i, e.target.value)}
                rows={5}
                className="w-full px-2 py-1 border rounded-md bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-gray-100 text-xs focus:outline-none focus:ring-2 focus:ring-blue-500 resize-y font-mono"
                placeholder='[{"toolId":"ado","enabled":true,"mode":"overlay","capabilities":["search"]}]'
              />
            </div>
          </CollapsibleWorkflowTypeCard>
        ))}
      </Section>

      {/* Secrets — stream-level; OCI Vault :name refs for ADO / GitHub / Graph / bot provisioning */}
      <Section
        title="Secrets"
        defaultOpen
        badge={`${secretEntries.length} key${secretEntries.length !== 1 ? 's' : ''}`}
        actions={
          <button
            type="button"
            onClick={addSecret}
            className="flex items-center gap-1 text-xs text-blue-600 dark:text-blue-400 hover:underline px-1"
          >
            <Plus className="w-3 h-3" /> Secret
          </button>
        }
      >
        {secretEntries.length === 0 && (
          <p className="text-xs text-gray-400 italic">No secrets configured. Add vault references as key → :secret-name pairs.</p>
        )}
        {secretEntries.map(([key, value], i) => (
          <div key={i} className="flex items-center gap-2">
            <Input
              value={key}
              onChange={(e) => updateSecretKey(key, e.target.value)}
              placeholder="key"
              className="text-xs !py-1 flex-1"
            />
            <span className="text-gray-400 text-xs">→</span>
            <Input
              value={value}
              onChange={(e) => updateSecretValue(key, e.target.value)}
              placeholder=":vault-secret-name"
              className="text-xs !py-1 flex-1"
            />
            <button type="button" onClick={() => removeSecret(key)} className="text-red-500 hover:text-red-700 shrink-0">
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          </div>
        ))}
      </Section>

      {/* Output Targets */}
      <Section
        title="Output Targets"
        badge={`${outputTargets.length} target${outputTargets.length !== 1 ? 's' : ''}`}
        actions={
          <button
            type="button"
            onClick={addOutputTarget}
            className="flex items-center gap-1 text-xs text-blue-600 dark:text-blue-400 hover:underline px-1"
          >
            <Plus className="w-3 h-3" /> Target
          </button>
        }
      >
        <p className="text-xs text-gray-500 dark:text-gray-400 leading-relaxed">
          Stream-level integrations (Jira project keys, HubSpot, SharePoint roots, email, etc.) used by triage, lobby, and prompt context.
          Each taxonomy workflow&apos;s <span className="font-mono text-[11px]">publish.steps[].target</span> names where that workflow&apos;s
          artifacts are routed (for example a SharePoint library id); it is separate from these rows and is evaluated when executing publish steps.
        </p>
        {outputTargets.length === 0 && (
          <p className="text-xs text-gray-400 italic">No output targets configured.</p>
        )}
        {outputTargets.map((ot, i) => (
          <div
            key={i}
            className="border border-gray-100 dark:border-gray-700/50 rounded-md p-2.5 space-y-2 bg-white dark:bg-gray-900/50"
          >
            <div className="flex items-center justify-between">
              <span className="text-xs font-semibold text-gray-700 dark:text-gray-300">{ot.type || `Target ${i + 1}`}</span>
              <button type="button" onClick={() => removeOutputTarget(i)} className="text-red-500 hover:text-red-700" title="Remove">
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </div>
            <div className="grid grid-cols-3 gap-2">
              <div>
                <label className="text-[11px] text-gray-400">Type</label>
                <Input value={ot.type} onChange={(e) => updateOutputTarget(i, { type: e.target.value })} placeholder="e.g. ado, github, teams" className="text-xs !py-1" />
              </div>
              <div>
                <label className="text-[11px] text-gray-400">Target</label>
                <Input value={ot.target} onChange={(e) => updateOutputTarget(i, { target: e.target.value })} placeholder="e.g. work-items, pr, channel" className="text-xs !py-1" />
              </div>
              <div>
                <label className="text-[11px] text-gray-400">Format</label>
                <Input
                  value={ot.format || ''}
                  onChange={(e) => updateOutputTarget(i, { format: e.target.value || undefined })}
                  placeholder="optional"
                  className="text-xs !py-1"
                />
              </div>
            </div>
          </div>
        ))}
      </Section>

      {/* Triage */}
      <Section title="Triage">
        <FieldRow label="Classifier Model">
          <Input value={data.triage.classifierModel} onChange={(e) => u({ triage: { ...data.triage, classifierModel: e.target.value } })} className="text-sm" />
        </FieldRow>
        <FieldRow label="Conversation Model">
          <Input
            value={data.triage.conversationModel || ''}
            onChange={(e) => u({ triage: { ...data.triage, conversationModel: e.target.value || undefined } })}
            placeholder="gpt-4o (default)"
            className="text-sm"
          />
        </FieldRow>
        <FieldRow label="Agent Model">
          <Input
            value={data.triage.agentModel || ''}
            onChange={(e) => u({ triage: { ...data.triage, agentModel: e.target.value || undefined } })}
            placeholder="default (Cursor default)"
            className="text-sm"
          />
        </FieldRow>
        <FieldRow label="Timeout (ms)">
          <Input
            type="number"
            value={data.triage.classifierTimeoutMs}
            onChange={(e) => u({ triage: { ...data.triage, classifierTimeoutMs: parseInt(e.target.value) || 0 } })}
            className="text-sm max-w-[160px]"
          />
        </FieldRow>
        <FieldRow label="Proceed Window">
          <div className="flex items-center gap-2">
            <Input
              type="number"
              value={data.triage.lanes.proceed_window_minutes}
              onChange={(e) => u({ triage: { ...data.triage, lanes: { ...data.triage.lanes, proceed_window_minutes: parseInt(e.target.value) || 0 } } })}
              className="text-sm max-w-[100px]"
            />
            <span className="text-xs text-gray-400">minutes</span>
          </div>
        </FieldRow>
        <FieldRow label="Escalation After">
          <div className="flex items-center gap-2">
            <Input
              type="number"
              value={data.triage.lanes.escalation_timeout_hours}
              onChange={(e) => u({ triage: { ...data.triage, lanes: { ...data.triage.lanes, escalation_timeout_hours: parseInt(e.target.value) || 0 } } })}
              className="text-sm max-w-[100px]"
            />
            <span className="text-xs text-gray-400">hours</span>
          </div>
        </FieldRow>
      </Section>

      <Section title="Model Routing">
        {(Object.keys(data.modelRouting) as Array<keyof ModelRoutingPolicy>).map((role) => (
          <div key={role} className="border border-gray-100 dark:border-gray-700/50 rounded-md p-2.5">
            <div className="text-xs font-semibold mb-2 text-gray-700 dark:text-gray-300">{role}</div>
            <div className="grid grid-cols-3 gap-2">
              {(['simple', 'medium', 'complex'] as RequestComplexity[]).map((complexity) => (
                <div key={`${role}-${complexity}`}>
                  <label className="text-[11px] text-gray-400">{complexity}</label>
                  <Input
                    value={data.modelRouting[role][complexity]}
                    onChange={(e) => updateModelRole(role, complexity, e.target.value)}
                    className="text-xs !py-1"
                  />
                </div>
              ))}
            </div>
          </div>
        ))}
      </Section>

      <Section title="Lobby Policy">
        <FieldRow label="Quick write max minutes">
          <Input
            type="number"
            value={data.lobbyPolicy.quickWriteMaxMinutes}
            onChange={(e) => u({ lobbyPolicy: { ...data.lobbyPolicy, quickWriteMaxMinutes: parseInt(e.target.value) || 0 } })}
            className="text-sm max-w-[120px]"
          />
        </FieldRow>
        <FieldRow label="Quick write max tool calls">
          <Input
            type="number"
            value={data.lobbyPolicy.quickWriteMaxToolCalls}
            onChange={(e) => u({ lobbyPolicy: { ...data.lobbyPolicy, quickWriteMaxToolCalls: parseInt(e.target.value) || 0 } })}
            className="text-sm max-w-[120px]"
          />
        </FieldRow>
        <FieldRow label="Allow repo changes">
          <input
            type="checkbox"
            checked={data.lobbyPolicy.allowRepoChanges}
            onChange={(e) => u({ lobbyPolicy: { ...data.lobbyPolicy, allowRepoChanges: e.target.checked } })}
          />
        </FieldRow>
        <FieldRow label="Allow PR merge">
          <input
            type="checkbox"
            checked={data.lobbyPolicy.allowPrMerge}
            onChange={(e) => u({ lobbyPolicy: { ...data.lobbyPolicy, allowPrMerge: e.target.checked } })}
          />
        </FieldRow>
        <FieldRow label="Allowed actions">
          <textarea
            value={data.lobbyPolicy.allowedWriteActions.join('\n')}
            onChange={(e) => u({
              lobbyPolicy: {
                ...data.lobbyPolicy,
                allowedWriteActions: e.target.value.split('\n').map((s) => s.trim()).filter(Boolean),
              },
            })}
            rows={6}
            className="w-full px-3 py-2 border rounded-md bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-gray-100 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-y"
          />
        </FieldRow>
      </Section>

      {/* Review */}
      <Section title="Review">
        <FieldRow label="Reviewer Role">
          <Input value={data.review.defaultReviewerRole} onChange={(e) => u({ review: { ...data.review, defaultReviewerRole: e.target.value } })} className="text-sm" />
        </FieldRow>
        <FieldRow label="Review Timeout">
          <div className="flex items-center gap-2">
            <Input
              type="number"
              value={data.review.reviewTimeoutHours}
              onChange={(e) => u({ review: { ...data.review, reviewTimeoutHours: parseInt(e.target.value) || 0 } })}
              className="text-sm max-w-[100px]"
            />
            <span className="text-xs text-gray-400">hours</span>
          </div>
        </FieldRow>
        <FieldRow label="Reminder After">
          <div className="flex items-center gap-2">
            <Input
              type="number"
              value={data.review.reminderAfterHours}
              onChange={(e) => u({ review: { ...data.review, reminderAfterHours: parseInt(e.target.value) || 0 } })}
              className="text-sm max-w-[100px]"
            />
            <span className="text-xs text-gray-400">hours</span>
          </div>
        </FieldRow>
      </Section>

      {/* Prompts */}
      <Section title="System Prompts">
        {(['classifierSystemPrompt', 'plannerSystemPrompt', 'authorSystemPrompt', 'reviewerSystemPrompt'] as const).map((key) => (
          <FieldRow key={key} label={key.replace('SystemPrompt', '').replace(/([A-Z])/g, ' $1').trim()}>
            <textarea
              value={data.prompts[key]}
              onChange={(e) => u({ prompts: { ...data.prompts, [key]: e.target.value } })}
              rows={3}
              className="w-full px-3 py-2 border rounded-md bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-gray-100 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-y"
            />
          </FieldRow>
        ))}
        <FieldRow label="Conversation">
          <textarea
            value={data.prompts.conversationSystemPrompt || ''}
            onChange={(e) => u({ prompts: { ...data.prompts, conversationSystemPrompt: e.target.value || undefined } })}
            rows={3}
            placeholder="System prompt for the conversational bot (auto-generated if empty)"
            className="w-full px-3 py-2 border rounded-md bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-gray-100 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-y"
          />
        </FieldRow>
        <FieldRow label="Context Triage">
          <textarea
            value={data.prompts.contextTriageSystemPrompt || ''}
            onChange={(e) => u({ prompts: { ...data.prompts, contextTriageSystemPrompt: e.target.value || undefined } })}
            rows={3}
            className="w-full px-3 py-2 border rounded-md bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-gray-100 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-y"
          />
        </FieldRow>

        {/* Lobby & Triage */}
        <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 mt-4 mb-1 uppercase tracking-wider">Lobby &amp; Triage</p>
        <FieldRow label="Lobby">
          <textarea
            value={data.prompts.lobbySystemPrompt || ''}
            onChange={(e) => u({ prompts: { ...data.prompts, lobbySystemPrompt: e.target.value || undefined } })}
            rows={3}
            placeholder="Lobby bot base instructions (overrides Conversation prompt for lobby)"
            className="w-full px-3 py-2 border rounded-md bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-gray-100 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-y"
          />
        </FieldRow>
        <FieldRow label="Triage Feedback">
          <textarea
            value={data.prompts.triageFeedbackPrompt || ''}
            onChange={(e) => u({ prompts: { ...data.prompts, triageFeedbackPrompt: e.target.value || undefined } })}
            rows={3}
            placeholder="Prompt sent back to user after triage classification"
            className="w-full px-3 py-2 border rounded-md bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-gray-100 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-y"
          />
        </FieldRow>
        <FieldRow label="Escalation">
          <textarea
            value={data.prompts.escalationPrompt || ''}
            onChange={(e) => u({ prompts: { ...data.prompts, escalationPrompt: e.target.value || undefined } })}
            rows={3}
            placeholder="Message template when workflow is escalated to human"
            className="w-full px-3 py-2 border rounded-md bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-gray-100 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-y"
          />
        </FieldRow>

        {/* Execution */}
        <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 mt-4 mb-1 uppercase tracking-wider">Execution</p>
        <FieldRow label="Execution Agent">
          <textarea
            value={data.prompts.executionSystemPrompt || ''}
            onChange={(e) => u({ prompts: { ...data.prompts, executionSystemPrompt: e.target.value || undefined } })}
            rows={3}
            placeholder="System prompt for Cursor execution agents"
            className="w-full px-3 py-2 border rounded-md bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-gray-100 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-y"
          />
        </FieldRow>
        <FieldRow label="Execution Follow-up">
          <textarea
            value={data.prompts.executionFollowupSystemPrompt || ''}
            onChange={(e) => u({ prompts: { ...data.prompts, executionFollowupSystemPrompt: e.target.value || undefined } })}
            rows={3}
            className="w-full px-3 py-2 border rounded-md bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-gray-100 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-y"
          />
        </FieldRow>
        <FieldRow label="Quick Write">
          <textarea
            value={data.prompts.quickWriteSystemPrompt || ''}
            onChange={(e) => u({ prompts: { ...data.prompts, quickWriteSystemPrompt: e.target.value || undefined } })}
            rows={3}
            placeholder="System prompt for lobby quick-write executor"
            className="w-full px-3 py-2 border rounded-md bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-gray-100 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-y"
          />
        </FieldRow>
        <FieldRow label="Ingest">
          <textarea
            value={data.prompts.ingestSystemPrompt || ''}
            onChange={(e) => u({ prompts: { ...data.prompts, ingestSystemPrompt: e.target.value || undefined } })}
            rows={3}
            placeholder="System prompt for input normalization / ingest stage"
            className="w-full px-3 py-2 border rounded-md bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-gray-100 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-y"
          />
        </FieldRow>
        <FieldRow label="Hierarchy">
          <textarea
            value={data.prompts.hierarchySystemPrompt || ''}
            onChange={(e) => u({ prompts: { ...data.prompts, hierarchySystemPrompt: e.target.value || undefined } })}
            rows={3}
            className="w-full px-3 py-2 border rounded-md bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-gray-100 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-y"
          />
        </FieldRow>
        <FieldRow label="Publish">
          <textarea
            value={data.prompts.publishSystemPrompt || ''}
            onChange={(e) => u({ prompts: { ...data.prompts, publishSystemPrompt: e.target.value || undefined } })}
            rows={3}
            className="w-full px-3 py-2 border rounded-md bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-gray-100 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-y"
          />
        </FieldRow>
      </Section>
    </div>
  );
}

// ---------------------------------------------------------------------------
// YAML view (original)
// ---------------------------------------------------------------------------

export function YamlEditorView({
  content,
  onChange,
  parseError,
}: {
  content: string;
  onChange: (v: string) => void;
  parseError: string | null;
}) {
  return (
    <div className="space-y-2">
      <div className="bg-gray-900 rounded-lg overflow-hidden border-2 border-gray-700 focus-within:border-blue-500">
        <div className="max-h-[600px] overflow-auto custom-scrollbar">
          <Editor
            value={content}
            onValueChange={onChange}
            highlight={(code: string) => highlight(code, languages.yaml, 'yaml')}
            padding={16}
            style={{
              fontFamily: '"Fira Code", "Fira Mono", monospace',
              fontSize: 14,
              backgroundColor: '#1e293b',
              color: '#e2e8f0',
              minHeight: '400px',
            }}
            className="outline-none"
          />
        </div>
      </div>
      {parseError && <p className="text-sm text-red-600 dark:text-red-400">Invalid YAML: {parseError}</p>}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Conversion helpers
// ---------------------------------------------------------------------------

export function yamlToFormData(yamlContent: string): StreamFormData {
  const parsed = yaml.load(yamlContent) as any;
  const root = parsed?.stream || parsed || {};

  const parseProducts = (raw: any[]): ProductConfig[] => {
    if (!Array.isArray(raw)) return [];
    return raw.map((p: any) => ({
      id: p.id || '',
      displayName: p.displayName || p.display_name || undefined,
      teamTypes: (p.teamTypes || p.team_types || []).map((tt: any) => ({
        id: tt.id || '',
        tenants: (tt.tenants || []).map((t: any) => ({
          code: t.code || '',
          displayName: t.displayName || t.display_name || undefined,
          agentTitle: t.agentTitle || t.agent_title || undefined,
          agentDescription: t.agentDescription || t.agent_description || undefined,
          parentRef: t.parentRef || t.parent_ref || undefined,
          adoProject: t.adoProject || t.ado_project || '',
          adoAreaPath: t.adoAreaPath || t.ado_area_path || undefined,
          repo: t.repo || '',
          bot: {
            enabled: t.bot?.enabled ?? false,
            displayName: t.bot?.displayName || t.bot?.display_name || '',
            defaultChannelId: t.bot?.defaultChannelId || t.bot?.default_channel_id || undefined,
            escalationChannelId: t.bot?.escalationChannelId || t.bot?.escalation_channel_id || undefined,
          },
        })),
      })),
    }));
  };
  const parsedProducts = parseProducts(root.products || []);
  const parseProductDefinitions = (): ProductDefinition[] => {
    const raw = root.productDefinitions || root.product_definitions;
    if (Array.isArray(raw) && raw.length > 0) {
      return raw
        .map((entry: any) => ({
          id: String(entry?.id || '').trim(),
          displayName: String(entry?.displayName || entry?.display_name || '').trim() || undefined,
        }))
        .filter((entry: ProductDefinition) => entry.id);
    }
    return parsedProducts
      .map((product) => ({ id: product.id, displayName: product.displayName }))
      .filter((entry) => entry.id);
  };
  const parseTeamDefinitions = (): TeamDefinition[] => {
    const raw = root.teamDefinitions || root.team_definitions;
    if (Array.isArray(raw) && raw.length > 0) {
      return raw
        .map((entry: any) => ({
          id: String(entry?.id || '').trim(),
          displayName: String(entry?.displayName || entry?.display_name || '').trim() || undefined,
        }))
        .filter((entry: TeamDefinition) => entry.id);
    }
    const seen = new Map<string, TeamDefinition>();
    for (const product of parsedProducts) {
      for (const team of product.teamTypes) {
        if (!team.id || seen.has(team.id)) continue;
        seen.set(team.id, { id: team.id });
      }
    }
    return Array.from(seen.values());
  };
  const parseTenantDefinitions = (): TenantDefinition[] => {
    const raw = root.tenantDefinitions || root.tenant_definitions;
    if (Array.isArray(raw) && raw.length > 0) {
      return raw
        .map((entry: any) => ({
          code: String(entry?.code || '').trim(),
          displayName: String(entry?.displayName || entry?.display_name || '').trim() || undefined,
        }))
        .filter((entry: TenantDefinition) => entry.code);
    }
    const seen = new Map<string, TenantDefinition>();
    for (const product of parsedProducts) {
      for (const team of product.teamTypes) {
        for (const tenant of team.tenants) {
          if (!tenant.code || seen.has(tenant.code)) continue;
          seen.set(tenant.code, { code: tenant.code, displayName: tenant.displayName });
        }
      }
    }
    return Array.from(seen.values());
  };

  const rawTaxonomy = root.taxonomy || {};
  const rawWorkflowTypes = rawTaxonomy.workflowTypes || rawTaxonomy.workflow_types || [];
  const parsedWorkflowTypes: WorkflowType[] = Array.isArray(rawWorkflowTypes)
    ? rawWorkflowTypes.map((wt: any) => ({
        id: wt.id || '',
        label: wt.label || '',
        executionMode: wt.executionMode || wt.execution_mode || 'draft',
        stepExecution: wt.stepExecution || wt.step_execution || 'independent_agents',
        repoType: wt.repoType || wt.repo_type || 'project',
        reviewGate: wt.reviewGate || wt.review_gate || '',
        reviewMode: wt.reviewMode || wt.review_mode || undefined,
        description: wt.description || undefined,
        publish: wt.publish
          ? {
              steps: Array.isArray(wt.publish?.steps)
                ? wt.publish.steps.map((step: any) => ({
                    type: step?.type || '',
                    target: step?.target || undefined,
                    condition: step?.condition || undefined,
                  }))
                : [],
            }
          : undefined,
        prompts: wt.prompts ? { ...wt.prompts } : undefined,
        tools: Array.isArray(wt.tools) ? wt.tools : undefined,
      }))
    : [];

  const rawOutputTargets = root.outputTargets || root.output_targets || [];
  const parsedOutputTargets: OutputTarget[] = Array.isArray(rawOutputTargets)
    ? rawOutputTargets.map((ot: any) => ({
        type: ot.type || '',
        target: ot.target || '',
        format: ot.format || undefined,
      }))
    : [];

  const defaultModels = {
    simple: 'gpt-4o-mini',
    medium: 'gpt-4o',
    complex: 'gpt-4o',
  };
  const rawModelRouting = root.modelRouting || root.model_routing || {};
  const modelRouting: ModelRoutingPolicy = {
    lobbyConversation: { ...defaultModels, ...(rawModelRouting.lobbyConversation || {}) },
    lobbyQuickWrite: { ...defaultModels, ...(rawModelRouting.lobbyQuickWrite || {}) },
    planner: { ...defaultModels, ...(rawModelRouting.planner || {}) },
    triager: { ...defaultModels, ...(rawModelRouting.triager || {}) },
    cursorExecution: { ...defaultModels, ...(rawModelRouting.cursorExecution || {}) },
    reviewer: { ...defaultModels, ...(rawModelRouting.reviewer || {}) },
  };
  const lobbyPolicy: LobbyCapabilityPolicy = {
    quickWriteMaxMinutes: root.lobbyPolicy?.quickWriteMaxMinutes ?? root.lobby_policy?.quickWriteMaxMinutes ?? 1,
    quickWriteMaxToolCalls: root.lobbyPolicy?.quickWriteMaxToolCalls ?? root.lobby_policy?.quickWriteMaxToolCalls ?? 3,
    allowRepoChanges: root.lobbyPolicy?.allowRepoChanges ?? root.lobby_policy?.allowRepoChanges ?? true,
    allowPrMerge: root.lobbyPolicy?.allowPrMerge ?? root.lobby_policy?.allowPrMerge ?? true,
    allowedWriteActions: root.lobbyPolicy?.allowedWriteActions
      || root.lobby_policy?.allowedWriteActions
      || [],
  };
  const repositoryAccess: RepositoryAccessPolicy = {
    additionalRepositories: Array.isArray(root.repositoryAccess?.additionalRepositories || root.repository_access?.additional_repositories)
      ? (root.repositoryAccess?.additionalRepositories || root.repository_access?.additional_repositories).map((entry: any) => ({
        repository: entry?.repository || '',
        description: entry?.description || undefined,
        access: 'read_only',
        capabilities: Array.isArray(entry?.capabilities) ? entry.capabilities.filter(Boolean) : [],
      }))
      : [],
  };

  return {
    streamId: root.id || root.streamId || root.stream_id || '',
    displayName: root.displayName || root.display_name || '',
    enabled: root.enabled !== false,
    products: parsedProducts,
    productDefinitions: parseProductDefinitions(),
    teamDefinitions: parseTeamDefinitions(),
    tenantDefinitions: parseTenantDefinitions(),
    taxonomy: { workflowTypes: parsedWorkflowTypes },
    triage: {
      classifierModel: 'fast',
      classifierTimeoutMs: 30000,
      lanes: { proceed_window_minutes: 15, escalation_timeout_hours: 24 },
      ...root.triage,
    },
    modelRouting,
    lobbyPolicy,
    repositoryAccess,
    review: root.review || { defaultReviewerRole: 'reviewer', reviewTimeoutHours: 48, reminderAfterHours: 24 },
    outputTargets: parsedOutputTargets,
    prompts: {
      classifierSystemPrompt: '',
      plannerSystemPrompt: '',
      authorSystemPrompt: '',
      reviewerSystemPrompt: '',
      ...root.prompts,
    },
    secrets: (() => {
      const raw = root.secrets || {};
      const out: Record<string, string> = {};
      if (raw && typeof raw === 'object') {
        for (const [k, v] of Object.entries(raw as Record<string, unknown>)) {
          if (v === null || v === undefined) continue;
          out[k] = typeof v === 'string' ? v : String(v);
        }
      }
      return out;
    })(),
    learningEnabled: root.learningEnabled ?? root.learning_enabled ?? true,
    evolutionEnabled: root.evolutionEnabled ?? root.evolution_enabled ?? false,
    emailInboxes: root.emailInboxes || root.email_inboxes || undefined,
    meetingRecordings: root.meetingRecordings || root.meeting_recordings || undefined,
    jiraListeners: root.jiraListeners || root.jira_listeners || undefined,
  };
}

export function formDataToYaml(data: StreamFormData, baseYaml?: string): string {
  const synchronizedDefinitions = (() => {
    const productMap = new Map<string, ProductDefinition>();
    for (const entry of data.productDefinitions || []) {
      const id = String(entry.id || '').trim();
      if (!id) continue;
      productMap.set(id, {
        id,
        displayName: String(entry.displayName || '').trim() || undefined,
      });
    }

    const teamMap = new Map<string, TeamDefinition>();
    for (const entry of data.teamDefinitions || []) {
      const id = String(entry.id || '').trim();
      if (!id) continue;
      teamMap.set(id, {
        id,
        displayName: String(entry.displayName || '').trim() || undefined,
      });
    }

    const tenantMap = new Map<string, TenantDefinition>();
    for (const entry of data.tenantDefinitions || []) {
      const code = String(entry.code || '').trim();
      if (!code) continue;
      tenantMap.set(code, {
        code,
        displayName: String(entry.displayName || '').trim() || undefined,
      });
    }

    return {
      productDefinitions: Array.from(productMap.values()),
      teamDefinitions: Array.from(teamMap.values()),
      tenantDefinitions: Array.from(tenantMap.values()),
    };
  })();

  let baseDoc: any = {};
  try {
    baseDoc = baseYaml ? (yaml.load(baseYaml) as any) || {} : {};
  } catch {
    baseDoc = {};
  }

  const hasStreamWrapper = Boolean(baseDoc && typeof baseDoc === 'object' && baseDoc.stream && typeof baseDoc.stream === 'object');
  const baseRoot = hasStreamWrapper ? (baseDoc.stream || {}) : (baseDoc || {});
  const nextRoot: any = {
    ...baseRoot,
    id: data.streamId,
    displayName: data.displayName,
    enabled: data.enabled,
    products: [],
    productDefinitions: synchronizedDefinitions.productDefinitions.map((entry) => ({
      id: entry.id,
      ...(entry.displayName ? { displayName: entry.displayName } : {}),
    })),
    teamDefinitions: synchronizedDefinitions.teamDefinitions.map((entry) => ({
      id: entry.id,
      ...(entry.displayName ? { displayName: entry.displayName } : {}),
    })),
    tenantDefinitions: synchronizedDefinitions.tenantDefinitions.map((entry) => ({
      code: entry.code,
      ...(entry.displayName ? { displayName: entry.displayName } : {}),
    })),
    // Always persist secrets from the form. Using `undefined` here overwrote merged baseRoot.secrets and deleted vault refs on save.
    secrets: data.secrets,
    taxonomy: data.taxonomy?.workflowTypes?.length
        ? {
            workflowTypes: data.taxonomy.workflowTypes.map((wt) => ({
              id: wt.id,
              label: wt.label,
              executionMode: wt.executionMode,
              stepExecution: wt.stepExecution || 'independent_agents',
              repoType: wt.repoType,
              reviewGate: wt.reviewGate,
              ...(wt.reviewMode ? { reviewMode: wt.reviewMode } : {}),
              ...(wt.description ? { description: wt.description } : {}),
              ...(wt.publish ? { publish: wt.publish } : {}),
              ...(wt.prompts ? { prompts: wt.prompts } : {}),
              ...(wt.tools ? { tools: wt.tools } : {}),
            })),
          }
        : undefined,
    triage: data.triage,
    modelRouting: data.modelRouting,
    lobbyPolicy: data.lobbyPolicy,
    review: data.review,
    outputTargets: data.outputTargets?.length ? data.outputTargets : undefined,
    prompts: data.prompts,
    learningEnabled: data.learningEnabled,
    evolutionEnabled: data.evolutionEnabled,
    ...(data.emailInboxes?.length ? { emailInboxes: data.emailInboxes } : {}),
    ...(data.meetingRecordings ? { meetingRecordings: data.meetingRecordings } : {}),
    ...(data.jiraListeners?.length ? { jiraListeners: data.jiraListeners } : {}),
  };

  delete nextRoot.repositoryAccess;
  delete nextRoot.repository_access;

  const obj = hasStreamWrapper ? { ...baseDoc, stream: nextRoot } : nextRoot;
  return yaml.dump(obj, { lineWidth: 120, noRefs: true, skipInvalid: true });
}

// ---------------------------------------------------------------------------
// Main dialog
// ---------------------------------------------------------------------------

interface StreamConfigEditDialogProps {
  open: boolean;
  onClose: () => void;
  streamId: string;
  initialYaml: string;
  onSaved?: () => void;
}

export function StreamConfigEditDialog({
  open,
  onClose,
  streamId,
  initialYaml,
  onSaved,
}: StreamConfigEditDialogProps) {
  const [mode, setMode] = useState<'form' | 'yaml'>('form');
  const [yamlContent, setYamlContent] = useState(initialYaml);
  const [formData, setFormData] = useState<StreamFormData | null>(null);
  const [parseError, setParseError] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const syncFormFromYaml = useCallback((yamlStr: string) => {
    try {
      setFormData(yamlToFormData(yamlStr));
      setParseError(null);
    } catch (err: any) {
      setParseError(err.message);
    }
  }, []);

  useEffect(() => {
    if (open) {
      setYamlContent(initialYaml);
      syncFormFromYaml(initialYaml);
      setParseError(null);
      setSaveError(null);
      setMode('form');
    }
  }, [open, initialYaml, streamId, syncFormFromYaml]);

  const switchMode = (newMode: 'form' | 'yaml') => {
    if (newMode === mode) return;

    if (newMode === 'yaml' && formData) {
      const generated = formDataToYaml(formData, yamlContent);
      setYamlContent(generated);
    }

    if (newMode === 'form') {
      syncFormFromYaml(yamlContent);
    }

    setMode(newMode);
  };

  const handleYamlChange = (v: string) => {
    setYamlContent(v);
    try {
      yaml.load(v);
      setParseError(null);
    } catch (err: any) {
      setParseError(err.message);
    }
  };

  const handleSave = async () => {
    let finalYaml = yamlContent;
    if (mode === 'form' && formData) {
      finalYaml = formDataToYaml(formData, yamlContent);
    }

    try {
      yaml.load(finalYaml);
    } catch (err: any) {
      setParseError(err.message);
      return;
    }

    setSaving(true);
    setSaveError(null);
    try {
      await apiClient.put<{ success: boolean }>(`/api/admin/ai-config/streams/${encodeURIComponent(streamId)}`, {
        yaml: finalYaml,
      });
      onSaved?.();
      onClose();
    } catch (err: unknown) {
      setSaveError(err instanceof Error ? err.message : 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(next) => !next && onClose()}>
      <DialogContent maxWidth="4xl" className="max-h-[90vh] flex flex-col">
        <DialogHeader>
          <div className="flex items-center justify-between gap-4">
            <DialogTitle>Edit stream: {streamId}</DialogTitle>
            <div className="flex items-center border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden shrink-0">
              <button
                type="button"
                onClick={() => switchMode('form')}
                className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium transition-colors ${
                  mode === 'form'
                    ? 'bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300'
                    : 'text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800/50'
                }`}
              >
                <FormInput className="w-3.5 h-3.5" /> Form
              </button>
              <button
                type="button"
                onClick={() => switchMode('yaml')}
                className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium transition-colors ${
                  mode === 'yaml'
                    ? 'bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300'
                    : 'text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800/50'
                }`}
              >
                <Code className="w-3.5 h-3.5" /> YAML
              </button>
            </div>
          </div>
        </DialogHeader>
        <DialogBody className="flex-1 min-h-0 overflow-y-auto custom-scrollbar">
          {mode === 'form' && formData ? (
            <StreamFormView
              data={formData}
              onChange={(next) => {
                setFormData(next);
              }}
            />
          ) : (
            <YamlEditorView content={yamlContent} onChange={handleYamlChange} parseError={parseError} />
          )}
          {saveError && <p className="text-sm text-red-600 dark:text-red-400 mt-2">{saveError}</p>}
        </DialogBody>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button type="button" onClick={() => void handleSave()} disabled={saving}>
            {saving ? 'Saving…' : 'Save'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
